/**
 * learning/api.ts — the browser's client for the Learning Hub backend (/api/learning).
 *
 * Reuses the generic same-origin helpers from community/api.ts (the session cookie is the only
 * credential — no token in the browser, ever) and adds the typed calls the hub page makes. The
 * response interfaces mirror the backend SAFE shapes exactly (routes/learning.ts). The Learning Hub
 * family is ALWAYS mounted, so a 401 is a real signed-out state and any other non-OK is a real error
 * — we use the no-feature result union.
 */
import { apiGet, apiPost, type ApiResult } from '../community/api';

export type CompetencyLevel = 'Beginner' | 'Practitioner' | 'Advanced';
export type MakerType = 'Business user' | 'Maker' | 'Pro-developer';

/** One cached Microsoft Learn item as the hub renders it (backend LearnItem). */
export interface LearnItem {
  itemRef: string;
  title: string;
  summary: string;
  url: string;
  level: CompetencyLevel | null;
  productTags: string[];
  language: string; // e.g. 'en-us'; non-English is STATED in the UI (LH-F06)
}

/** The cached Learn feed + its staleness signals (backend CachedLearn). */
export interface CachedLearn {
  items: LearnItem[];
  /**
   * How many items the server's cache holds in total. `items` is a CAPPED page of it — the endpoint
   * used to send the lot, which was measured at 1.8 MB — so this is what the page must quote when it
   * says how much it is not showing. See learn/catalog.ts HUB_ITEM_LIMIT.
   */
  total: number;
  oldestFetchedAt: string | null;
  lastRun: { outcome: string | null; finishedAt: string | null; error: string | null } | null;
}

/** The hero training indicator (backend TrainingProgress). */
export interface TrainingProgress {
  percent: number;
  stepsCompleted: number;
  stepCount: number;
  knowledgeCheckPassed: boolean;
  state: 'not_started' | 'in_progress' | 'complete';
}

/** GET /api/learning/hub — the whole page payload in one call. */
export interface HubPayload {
  competencyLevel: CompetencyLevel | null;
  makerType: MakerType | null;
  assessed: boolean;
  /**
   * The person's job function, read from the corporate directory (LH-F10, read-only UR-F06). Display
   * only — it drives relevance (department-first examples), never access. Null when the directory has
   * no department for this person.
   */
  department: string | null;
  training: TrainingProgress;
  learn: CachedLearn;
  /**
   * The single most-adopted tool this week (decision #6) — what the "see what's possible / real
   * examples" intent tiles link at. Null when the week has no qualifying adoption; the tile then
   * falls back to /tools (never a dangling link). A safe {toolId,name} subset — names a tool, not a
   * person.
   */
  mostPopularTool: { toolId: string; name: string } | null;
}

/** One training step (backend TrainingStep). */
export interface TrainingStep {
  stepNo: number;
  title: string;
  adaptsByLevel: boolean;
  body: string[];
}

/**
 * One knowledge-check question as the browser receives it (backend PublicKnowledgeCheckQuestion).
 * There is NO answer key here and there must never be one (LH-F11): the server marks the check and
 * derives the score, so the browser cannot know — or assert — whether an answer was right.
 */
export interface KnowledgeCheckQuestion {
  id: string;
  prompt: string;
  options: string[];
}

/** GET /api/learning/training — the seven steps + this person's progress + the check. */
export interface TrainingPayload {
  steps: TrainingStep[];
  progress: TrainingProgress;
  questions: KnowledgeCheckQuestion[];
  passPct: number;
}

/** One catalogue example on the "See real examples" tile (backend /examples). */
export interface ExampleTool {
  toolId: string;
  name: string;
  description: string;
  ownerName: string;
  trustLabel: 'Approved' | 'Unverified';
  adoptionCount: number;
}

export interface ExamplesPayload {
  examples: ExampleTool[];
  enabled: boolean;
}

// ── the typed calls ────────────────────────────────────────────────────────────────────────────

export function getHub(): Promise<ApiResult<HubPayload>> {
  return apiGet<HubPayload>('/api/learning/hub');
}

/** GET /api/learning/search — live Microsoft Learn results (or cached, on upstream trouble). */
export interface SearchPayload {
  /** 'live' = fed straight from Microsoft this request; 'cache' = last-good fallback (state it). */
  source: 'live' | 'cache';
  items: LearnItem[];
  /** ISO: when live results were fetched, or (cache) the oldest cached item's age. */
  asOf: string | null;
  lastRun: { outcome: string | null; finishedAt: string | null; error: string | null } | null;
}

/**
 * Live search over Microsoft Learn, biased server-side to the person's assessed role+level (a lens).
 * `q` is sent as a query param, used for this request, and never stored anywhere (AE-F36). Pass
 * `unfiltered` for the "browse everything" escape hatch (no role/level bias).
 */
export function searchLearn(
  q: string,
  opts?: { product?: string; unfiltered?: boolean },
): Promise<ApiResult<SearchPayload>> {
  const params = new URLSearchParams();
  if (q.trim().length > 0) params.set('q', q.trim());
  if (opts?.product) params.set('product', opts.product);
  if (opts?.unfiltered) params.set('unfiltered', 'true');
  const qs = params.toString();
  return apiGet<SearchPayload>(`/api/learning/search${qs ? `?${qs}` : ''}`);
}

export function getTraining(): Promise<ApiResult<TrainingPayload>> {
  return apiGet<TrainingPayload>('/api/learning/training');
}

export function getExamples(): Promise<ApiResult<ExamplesPayload>> {
  return apiGet<ExamplesPayload>('/api/learning/examples');
}

/** Set the person's self-assessment result. Repeatable (LH-F08). 204 on success → body is null. */
export function submitSelfAssessment(
  level: CompetencyLevel,
  makerType?: MakerType,
): Promise<ApiResult<null>> {
  const body = makerType ? { level, makerType } : { level };
  return apiPost<null>('/api/learning/self-assessment', body);
}

/** Mark one training step complete; returns the updated progress. */
export function completeStep(stepNo: number): Promise<ApiResult<{ progress: TrainingProgress }>> {
  return apiPost<{ progress: TrainingProgress }>('/api/learning/training/step', { stepNo });
}

/** The server's mark: the score it derived, and pass/fail against the governed threshold (LH-F11). */
export interface KnowledgeCheckResult {
  passed: boolean;
  passPct: number;
  /** 0..100, marked SERVER-SIDE. The browser has no answer key and computes nothing. */
  scorePct: number;
  progress: TrainingProgress;
}

/**
 * Submit the chosen ANSWERS — one option index per question, in the order `questions` arrived — and
 * receive the server's mark. The score and pass/fail are the server's findings, not ours (LH-F11):
 * this is the platform's mandatory training, so a browser must not be able to influence its own result.
 */
export function submitKnowledgeCheck(answers: number[]): Promise<ApiResult<KnowledgeCheckResult>> {
  return apiPost<KnowledgeCheckResult>('/api/learning/training/knowledge-check', { answers });
}

/**
 * Record that the person opened an outbound Learn link (LH-F13). Fire-and-forget: the click must not
 * wait on this, and a failure to record must never block navigation. We deliberately ignore the
 * result.
 */
export function recordLinkOpen(itemRef: string): void {
  void apiPost<null>('/api/learning/link-open', { itemRef });
}
